import { ErrandResponse } from '@backend/schemas/src/errands'
import { PlusIcon, Upload } from 'lucide-react'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrandStatusLabel } from '@/components/molecules/errand-status-label'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type Props = {
  errand: ErrandResponse
  onAddNote(): void
  onChangeStatus(status: ErrandResponse['status']): void
  onUpload(): void
}

/**
 * Actions that add to the errand's event list — changing status, uploading a document, or adding
 * a note. Two labeled sub-sections side by side under a "Hantera" heading (see errands.$id.tsx).
 * The status select uses the plain default Select styling — same as the "Din relation till
 * personen" dropdown on the add-individual flow — rather than a custom compact trigger.
 */
export const ErrandEventActions: FC<Props> = ({ errand, onAddNote, onChangeStatus, onUpload }) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap items-start gap-6">
      <Field className="w-fit min-w-[220px]">
        <FieldLabel htmlFor="errand-status">{t('errandsPage.info.statusLabel')}</FieldLabel>
        <Select onValueChange={onChangeStatus} value={errand.status}>
          <SelectTrigger id="errand-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="started">
                <ErrandStatusLabel status="started" />
              </SelectItem>
              <SelectItem value="submitted">
                <ErrandStatusLabel status="submitted" />
              </SelectItem>
              <SelectItem value="approved">
                <ErrandStatusLabel status="approved" />
              </SelectItem>
              <SelectItem value="rejected">
                <ErrandStatusLabel status="rejected" />
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </Field>

      <Field className="w-fit">
        <FieldLabel>{t('errandsPage.info.knowledgeLabel')}</FieldLabel>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onUpload} variant="outline">
            <Upload size={16} />
            {t('errandsPage.info.uploadCta')}
          </Button>
          <Button onClick={onAddNote} variant="outline">
            <PlusIcon size={16} />
            {t('errandsPage.info.addNoteCta')}
          </Button>
        </div>
      </Field>
    </div>
  )
}

export default ErrandEventActions
